/**
 * Tip Amount Modal - Apple Cash-inspired UI
 * Appears at bottom like keyboard replacement
 * Dark modal with metallic amount display and +/- controls
 *
 * MOONS CHANGES vs CutieCaryn original:
 * - CHANGED: MIN_AMOUNT from 3 to 1 (Moons range)
 * - CHANGED: MAX_AMOUNT from 1000 to 500 (Moons range)
 * - CHANGED: DEFAULT_AMOUNT from 50 to 10
 * - CHANGED: PRESET_AMOUNTS from [20, 50, 100] to MOON_TIP_PRESETS [5, 10, 25, 50, 100]
 * - CHANGED: '$' prefix replaced with CURRENCY_SYMBOL (moon emoji) in all displays
 * - KEPT: All UI logic, animation, increment/decrement, keypad — unchanged
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
// [MOONS] Import Moon presets and symbol — CutieCaryn uses hardcoded $ values
import { MOON_TIP_PRESETS, CURRENCY_SYMBOL } from '@/config/moonConfig'

// [MOONS] CutieCaryn values: MIN=3, MAX=1000, DEFAULT=50, PRESETS=[20,50,100]
const MIN_AMOUNT = 1
const MAX_AMOUNT = 500
const DEFAULT_AMOUNT = 10

const PRESET_AMOUNTS = [...MOON_TIP_PRESETS]

interface TipAmountModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (amount: number) => void
}

export function TipAmountModal({ isOpen, onClose, onConfirm }: TipAmountModalProps) {
  const [amount, setAmount] = useState(DEFAULT_AMOUNT)
  const [showKeypad, setShowKeypad] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setAmount(DEFAULT_AMOUNT)
      setShowKeypad(false)
      setInputValue('')
      // Blur any active input to dismiss mobile keyboard
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }
  }, [isOpen])

  // Focus input when keypad is shown
  useEffect(() => {
    if (showKeypad && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showKeypad])

  // Increment - go to next multiple of 5
  const increment = () => {
    setAmount(prev => {
      if (prev >= MAX_AMOUNT) return MAX_AMOUNT
      // If not divisible by 5, go to next multiple of 5
      if (prev % 5 !== 0) {
        return Math.min(MAX_AMOUNT, Math.ceil(prev / 5) * 5)
      }
      // Otherwise add 5
      return Math.min(MAX_AMOUNT, prev + 5)
    })
  }

  // Decrement - go to previous multiple of 5
  const decrement = () => {
    setAmount(prev => {
      if (prev <= MIN_AMOUNT) return MIN_AMOUNT
      // If not divisible by 5, go to previous multiple of 5
      if (prev % 5 !== 0) {
        const lower = Math.floor(prev / 5) * 5
        return Math.max(MIN_AMOUNT, lower)
      }
      // Otherwise subtract 5
      return Math.max(MIN_AMOUNT, prev - 5)
    })
  }

  const setPresetAmount = (preset: number) => {
    setAmount(preset)
  }

  const handleKeypadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '')
    setInputValue(value)
    const numValue = parseInt(value) || 0
    if (numValue >= MIN_AMOUNT && numValue <= MAX_AMOUNT) {
      setAmount(numValue)
    } else if (numValue > MAX_AMOUNT) {
      setAmount(MAX_AMOUNT)
      setInputValue(MAX_AMOUNT.toString())
    } else if (numValue > 0 && numValue < MIN_AMOUNT) {
      setAmount(numValue) // Allow typing, will validate on confirm
    }
  }

  const handleKeypadBlur = () => {
    if (amount < MIN_AMOUNT) {
      setAmount(MIN_AMOUNT)
      setInputValue(MIN_AMOUNT.toString())
    }
  }

  const handleConfirm = () => {
    if (amount >= MIN_AMOUNT && amount <= MAX_AMOUNT) {
      onConfirm(amount)
    }
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col justify-end"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        touchAction: 'none'
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      {/* Bottom sheet modal */}
      <div
        className="relative w-full rounded-t-3xl overflow-hidden animate-slide-up"
        style={{
          background: 'linear-gradient(180deg, #1c1c1e 0%, #0a0a0a 100%)',
          maxHeight: 'calc(100vh - env(safe-area-inset-top, 20px) - 60px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="pt-4 pb-2 px-6 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-[#0a84ff] text-base font-normal"
          >
            Cancel
          </button>
          <span className="text-white font-semibold text-lg">Tip</span>
          <div className="w-14" /> {/* Spacer for centering */}
        </div>

        {/* Preset Amount Pills */}
        <div className="px-6 pt-3 flex justify-center gap-3">
          {PRESET_AMOUNTS.map(preset => (
            <button
              key={preset}
              onClick={() => setPresetAmount(preset)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                amount === preset
                  ? 'bg-white/20 text-white border border-white/30'
                  : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
              }`}
            >
              {CURRENCY_SYMBOL} {preset}
            </button>
          ))}
        </div>

        {/* Amount Display */}
        <div className="py-8 px-6 flex items-center justify-center gap-8">
          {/* Minus Button */}
          <button
            onClick={decrement}
            disabled={amount <= MIN_AMOUNT}
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              amount <= MIN_AMOUNT
                ? 'bg-[#2c2c2e] text-gray-600 cursor-not-allowed'
                : 'bg-[#3a3a3c] text-gray-300 active:scale-95'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" d="M5 12h14" />
            </svg>
          </button>

          {/* Amount Display - clickable to edit */}
          <div className="flex items-baseline min-w-[140px] justify-center">
            {showKeypad ? (
              <div className="flex items-baseline">
                <span
                  className="text-4xl font-semibold mr-1"
                  style={{
                    color: '#f0f0f0',
                    textShadow: '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.1)'
                  }}
                >
                  {CURRENCY_SYMBOL}
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={inputValue || amount.toString()}
                  onChange={handleKeypadChange}
                  onBlur={handleKeypadBlur}
                  className="w-24 text-5xl font-semibold bg-transparent border-none outline-none text-center"
                  style={{
                    color: '#f0f0f0',
                    textShadow: '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.1)',
                    caretColor: '#ffffff'
                  }}
                />
              </div>
            ) : (
              <button
                onClick={() => {
                  setShowKeypad(true)
                  setInputValue(amount.toString())
                }}
                className="text-5xl font-semibold tracking-tight hover:opacity-80 transition-opacity"
                style={{
                  color: '#f0f0f0',
                  textShadow: '0 0 20px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.1)'
                }}
              >
                {CURRENCY_SYMBOL} {amount}
              </button>
            )}
          </div>

          {/* Plus Button */}
          <button
            onClick={increment}
            disabled={amount >= MAX_AMOUNT}
            className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              amount >= MAX_AMOUNT
                ? 'bg-[#2c2c2e] text-gray-600 cursor-not-allowed'
                : 'bg-[#3a3a3c] text-gray-300 active:scale-95'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>

        {/* Show Keypad Toggle - only show when not already in keypad mode */}
        {!showKeypad && (
          <div className="pb-3 text-center">
            <button
              onClick={() => {
                setShowKeypad(true)
                setInputValue(amount.toString())
              }}
              className="text-[#0a84ff] text-sm font-normal"
            >
              Show Keypad
            </button>
          </div>
        )}

        {/* Hide Keypad - only show when in keypad mode */}
        {showKeypad && (
          <div className="pb-3 text-center">
            <button
              onClick={() => setShowKeypad(false)}
              className="text-[#0a84ff] text-sm font-normal"
            >
              Done
            </button>
          </div>
        )}

        {/* Send Button - Apple style */}
        <div className="px-6 pb-8 pt-2">
          <button
            onClick={handleConfirm}
            disabled={amount < MIN_AMOUNT || amount > MAX_AMOUNT}
            className="w-full py-4 rounded-xl font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-40"
            style={{
              background: '#3a3a3c',
              color: amount >= MIN_AMOUNT && amount <= MAX_AMOUNT ? '#ffffff' : '#666'
            }}
          >
            Send
          </button>
        </div>

        {/* Home indicator bar */}
        <div className="pb-2 flex justify-center">
          <div className="w-32 h-1 bg-white/20 rounded-full" />
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  )

  return createPortal(modalContent, document.body)
}
